import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Circle, Mail, UserCheck, Zap, Users, Send, ChevronDown, ChevronUp } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { cadastroAPI } from '../services/api';

const steps = [
  { key: 'conta_criada', label: 'Criar conta', icon: UserCheck, route: null },
  { key: 'email_confirmado', label: 'Confirmar email', icon: Mail, route: null },
  { key: 'cadastro_completo', label: 'Completar cadastro', icon: UserCheck, route: null },
  { key: 'canal_conectado', label: 'Conectar WhatsApp', icon: Zap, route: '/configuracoes/canais' },
  { key: 'contatos_importados', label: 'Importar contatos', icon: Users, route: '/contatos' },
  { key: 'campanha_enviada', label: 'Enviar primeira campanha', icon: Send, route: '/marketing/campanhas/nova' },
];

const OnboardingWizard = ({ clienteId }) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (!clienteId) return;
    cadastroAPI.onboarding(clienteId).then(r => setData(r.data)).catch(() => {});
  }, [clienteId]);

  if (!data) return null;

  const completados = steps.filter(s => data[s.key]).length;
  const total = steps.length;
  const pct = Math.round((completados / total) * 100);

  // Esconder se tudo completo
  if (completados === total) return null;

  const proximoPasso = steps.find(s => !data[s.key]);

  return (
    <div className={`rounded-2xl overflow-hidden ${darkMode ? 'bg-zinc-800/80 border border-zinc-700/50' : 'bg-white border border-gray-200 shadow-sm'}`}>
      <button onClick={() => setCollapsed(!collapsed)}
        className={`w-full px-5 py-4 flex items-center justify-between ${darkMode ? 'hover:bg-zinc-700/50' : 'hover:bg-gray-50'} transition-colors`}>
        <div className="flex items-center gap-3">
          <div className="relative w-10 h-10">
            <svg className="w-10 h-10 -rotate-90">
              <circle cx="20" cy="20" r="16" fill="none" stroke={darkMode ? '#3f3f46' : '#e5e7eb'} strokeWidth="3" />
              <circle cx="20" cy="20" r="16" fill="none" stroke="#f97316" strokeWidth="3"
                strokeDasharray={`${pct} ${100 - pct}`} strokeDashoffset="0" strokeLinecap="round" />
            </svg>
            <span className={`absolute inset-0 flex items-center justify-center text-xs font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{completados}/{total}</span>
          </div>
          <div className="text-left">
            <p className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Configure sua plataforma</p>
            <p className={`text-xs ${darkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
              {proximoPasso ? `Próximo: ${proximoPasso.label}` : 'Quase lá!'}
            </p>
          </div>
        </div>
        {collapsed ? <ChevronDown className={`h-4 w-4 ${darkMode ? 'text-zinc-400' : 'text-gray-400'}`} /> : <ChevronUp className={`h-4 w-4 ${darkMode ? 'text-zinc-400' : 'text-gray-400'}`} />}
      </button>

      {!collapsed && (
        <div className={`px-5 pb-4 space-y-1 border-t ${darkMode ? 'border-zinc-700/50' : 'border-gray-100'}`}>
          {steps.map((step, i) => {
            const done = data[step.key];
            const Icon = step.icon;
            const isNext = !done && step === proximoPasso;
            return (
              <div key={step.key}
                onClick={() => !done && step.route && navigate(step.route)}
                className={`flex items-center gap-3 py-2.5 px-3 rounded-xl transition-all ${
                  done ? '' :
                  isNext ? (darkMode ? 'bg-orange-500/10 cursor-pointer' : 'bg-orange-50 cursor-pointer') :
                  step.route ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-zinc-700/50' : ''
                }`}>
                {done ? (
                  <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                    <Check className="h-3.5 w-3.5 text-white" />
                  </div>
                ) : (
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${
                    isNext ? 'border-orange-500 text-orange-500' : darkMode ? 'border-zinc-600 text-zinc-600' : 'border-gray-300 text-gray-300'
                  }`}>
                    <span className="text-xs font-bold">{i + 1}</span>
                  </div>
                )}
                <span className={`text-sm ${
                  done ? (darkMode ? 'text-zinc-500 line-through' : 'text-gray-400 line-through') :
                  isNext ? 'text-orange-500 font-medium' :
                  darkMode ? 'text-zinc-300' : 'text-gray-700'
                }`}>{step.label}</span>
                {isNext && step.route && (
                  <span className={`ml-auto text-xs font-medium px-2 py-0.5 rounded-full bg-orange-500 text-white`}>Fazer agora</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OnboardingWizard;
